'use client';

/**
 * Single place for app navigation: import useRouter and Link from here so base path is applied
 * automatically (e.g. push('/login') → /c/rutgers/login when on a company path). Re-exports
 * usePathname, useParams, useSearchParams unchanged. Query-only or empty hrefs are passed through.
 */
import NextLink from 'next/link';
import {
  usePathname as nextUsePathname,
  useParams as nextUseParams,
  useSearchParams as nextUseSearchParams,
  useRouter as nextUseRouter,
} from 'next/navigation';
import { useMemo } from 'react';
import { useBasePathContext } from '@/providers/BasePathProvider';
import { stripCompanyPrefix } from '@/lib/base-url-core';
import type { ComponentProps } from 'react';

/**
 * Returns the pathname with the /c/{companyId} prefix stripped so route comparisons
 * (e.g. pathname === '/myaccount/profile') work on both company and non-company paths.
 * BasePathProvider uses next/navigation's usePathname directly to keep the full path.
 */
export function usePathname(): string {
  const raw = nextUsePathname();
  return stripCompanyPrefix(raw ?? '');
}
export const useParams = nextUseParams;
export const useSearchParams = nextUseSearchParams;

/**
 * Base-path-aware router. push(href) and replace(href) resolve href under the current base
 * (e.g. push('/login') -> /c/rutgers/login when on a company path).
 */
export function useRouter() {
  const nextRouter = nextUseRouter();
  const { resolvePath } = useBasePathContext();
  return useMemo(
    () => ({
      ...nextRouter,
      push: (href: string, options?: { scroll?: boolean }) =>
        typeof href === 'string' && (href === '' || href.startsWith('?'))
          ? nextRouter.push(href, options)
          : nextRouter.push(resolvePath(href), options),
      replace: (href: string, options?: { scroll?: boolean }) =>
        typeof href === 'string' && (href === '' || href.startsWith('?'))
          ? nextRouter.replace(href, options)
          : nextRouter.replace(resolvePath(href), options),
    }),
    [nextRouter, resolvePath]
  );
}

type NextLinkProps = ComponentProps<typeof NextLink>;

/**
 * Base-path-aware Link. href is resolved under the current base so links work at both
 * /path and /c/companyId/path without per-page handling.
 */
export function Link({ href, ...rest }: NextLinkProps) {
  const { resolvePath } = useBasePathContext();
  const resolvedHref =
    typeof href === 'string'
      ? resolvePath(href)
      : href && typeof href === 'object' && 'pathname' in href
        ? { ...href, pathname: resolvePath(href.pathname ?? '/') }
        : href;
  return <NextLink href={resolvedHref} {...rest} />;
}
